import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    ArrowLeft, Coins, Loader2, Trophy, Users, Spade, Clock, LogIn,
    Crown, AlertCircle, Zap, Timer, Ticket, X,
} from "lucide-react";
const CHIPS_PER_CREDIT = 1;   // must match backend TOURNAMENT_CHIP_MULTIPLIER

const SUIT_CHAR = { s: "\u2660", h: "\u2665", d: "\u2666", c: "\u2663" };
const SUIT_COLOR = { s: "#0A0A0A", c: "#0A0A0A", h: "#E11D48", d: "#E11D48" };

function Card({ card, hidden, small }) {
    const size = small ? "w-9 h-12 text-sm" : "w-12 h-16 text-lg sm:w-14 sm:h-20 sm:text-xl";
    if (!card || hidden || card === "??") {
        return (
            <div className={`${size} bg-vibrantblue border-[3px] border-ink rounded-lg shadow-brutal flex items-center justify-center`}>
                <div className="w-full h-full bg-[repeating-linear-gradient(45deg,#2563EB,#2563EB_4px,#1E40AF_4px,#1E40AF_8px)] rounded-md" />
            </div>
        );
    }
    const r = card[0], s = card[1];
    const rank = r === "T" ? "10" : r;
    return (
        <div className={`${size} bg-white border-[3px] border-ink rounded-lg shadow-brutal flex flex-col items-center justify-center font-heading font-black`} style={{ color: SUIT_COLOR[s] }}>
            <div className="leading-none">{rank}</div>
            <div className="leading-none">{SUIT_CHAR[s]}</div>
        </div>
    );
}

function chips(n) {
    if (n == null) return "0";
    // chips shown in credits (divide by 2 with 1-decimal max)
    const v = n / CHIPS_PER_CREDIT;
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function fmtCountdown(ms) {
    if (ms <= 0) return "starting…";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
    return `${sec}s`;
}

function TournamentCard({ t, mine, isLoggedIn, credits, onRegister, onUnregister }) {
    const [buyIn, setBuyIn] = useState(100);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const i = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(i);
    }, []);

    const startMs = new Date(t.starts_at).getTime();
    const untilStart = startMs - now;
    const isFull = false; // multi-table: no per-tournament cap
    const canRegister = isLoggedIn && t.status === "SCHEDULED" && !mine && !isFull;

    const submit = async (action, args) => {
        setErr("");
        setBusy(true);
        try {
            await action(...args);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={`tournament-${t.id}`} className="brutal-card p-5 bg-white">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Spade className="w-5 h-5 text-ink" strokeWidth={2.5} />
                    <h3 className="font-heading font-black text-xl text-ink tracking-tighter">
                        6-Max Tournament
                    </h3>
                    <span className={`font-heading font-bold text-[10px] uppercase tracking-widest border-[2px] border-ink rounded px-2 py-0.5 ${t.status === "RUNNING" ? "bg-vibrantpink text-white" : t.status === "SCHEDULED" ? "bg-vibrantyellow" : t.status === "COMPLETE" ? "bg-emerald-600 text-white" : "bg-ink text-white"}`}>
                        {t.status.toLowerCase()}
                    </span>
                </div>
                <div className="flex items-center gap-3 font-heading text-sm">
                    <span className="flex items-center gap-1 text-ink"><Users className="w-4 h-4" strokeWidth={2.5} /> {t.seats_taken} registered</span>
                    <span className="flex items-center gap-1 text-vibrantorange"><Coins className="w-4 h-4" strokeWidth={2.5} /> {t.prize_pool}</span>
                </div>
            </div>

            {t.status === "SCHEDULED" && (
                <div className="flex items-center gap-2 mb-3 font-body text-sm text-ink/70">
                    <Timer className="w-4 h-4" strokeWidth={2.5} />
                    <span>Starts in <span className="font-heading font-bold text-ink" data-testid={`tournament-countdown-${t.id}`}>{fmtCountdown(untilStart)}</span></span>
                    <span className="text-ink/40">·</span>
                    <span>Blinds start 1/2 · double every 2 min</span>
                </div>
            )}
            {t.status === "RUNNING" && t.blinds && (
                <div className="flex items-center gap-2 mb-3 font-body text-sm text-ink/70">
                    <Zap className="w-4 h-4 text-vibrantorange" strokeWidth={2.5} />
                    <span>Level {t.blind_level + 1} · blinds {t.blinds[0] / CHIPS_PER_CREDIT}/{t.blinds[1] / CHIPS_PER_CREDIT}</span>
                </div>
            )}

            {t.players && t.players.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {t.players.map((p) => (
                        <span key={p.user_id} data-testid={`tournament-player-${p.username}`} className="inline-flex items-center gap-1 bg-cream border-[2px] border-ink rounded-lg px-2 py-1 font-heading font-bold text-xs text-ink">
                            {p.username} · {p.buy_in}c
                        </span>
                    ))}
                </div>
            )}

            {t.status === "COMPLETE" && t.payouts && t.payouts.length > 0 && (
                <div className="mt-2 space-y-1">
                    {t.payouts.map((pay) => (
                        <div key={pay.place} className="flex items-center gap-2 font-body text-sm">
                            <Crown className={`w-4 h-4 ${pay.place === 1 ? "text-vibrantyellow" : pay.place === 2 ? "text-slate-400" : "text-orange-700"}`} strokeWidth={2.5} fill="currentColor" />
                            <span className="font-heading font-bold text-ink">{pay.place}. {pay.username}</span>
                            <span className="ml-auto text-vibrantorange font-heading font-bold">+{pay.credits}c</span>
                        </div>
                    ))}
                </div>
            )}

            {canRegister && (
                <div className="mt-3 border-t-[3px] border-ink/10 pt-3">
                    <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">
                        Buy-in (3 – 10,000 credits · you'll get {buyIn} chips)
                    </label>
                    <div className="flex gap-2 items-stretch">
                        <input
                            type="number"
                            min={3}
                            max={10000}
                            value={buyIn}
                            onChange={(e) => setBuyIn(Math.max(3, Math.min(10000, parseInt(e.target.value) || 3)))}
                            data-testid={`tournament-buyin-input-${t.id}`}
                            className="flex-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 font-heading font-black text-lg text-ink focus:outline-none"
                        />
                        <button
                            type="button"
                            disabled={busy || (credits != null && credits < buyIn)}
                            onClick={() => submit(onRegister, [t.id, buyIn])}
                            data-testid={`tournament-register-btn-${t.id}`}
                            className="brutal-btn-primary text-sm py-2 px-4 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : <><Ticket className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Register</>}
                        </button>
                    </div>
                    {credits != null && credits < buyIn && (
                        <p className="mt-2 font-body text-xs text-vibrantpink">Not enough credits ({credits} available).</p>
                    )}
                </div>
            )}

            {mine && t.status === "SCHEDULED" && (
                <div className="mt-3 flex items-center justify-between gap-3 border-t-[3px] border-ink/10 pt-3">
                    <span className="font-heading font-bold text-sm text-emerald-600">
                        <Ticket className="w-4 h-4 inline mr-1" strokeWidth={2.5} />
                        You're in for {mine.buy_in} credits
                    </span>
                    <button
                        type="button"
                        onClick={() => submit(onUnregister, [t.id])}
                        disabled={busy}
                        data-testid={`tournament-unregister-btn-${t.id}`}
                        className="brutal-btn-white text-xs py-1.5 px-3 disabled:opacity-50"
                    >
                        Withdraw
                    </button>
                </div>
            )}

            {mine && t.status === "RUNNING" && (
                <div className="mt-3 border-t-[3px] border-ink/10 pt-3">
                    <Link
                        to={`/poker/tournament/${t.id}`}
                        data-testid={`tournament-enter-btn-${t.id}`}
                        className="brutal-btn-primary w-full text-sm py-2"
                    >
                        <Spade className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Enter table
                    </Link>
                </div>
            )}

            {err && (
                <div className="mt-3 flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                    <span className="font-body font-semibold text-xs">{err}</span>
                </div>
            )}
        </div>
    );
}

export default function PokerTournaments() {
    const { user, loading: authLoading, refresh } = useAuth();
    const [tournaments, setTournaments] = useState({ live: [], recent: [] });
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(true);

    const isLoggedIn = user && user !== false;

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/poker/tournaments");
            setTournaments(data);
        } catch {}
    }, []);

    const loadBalance = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const { data } = await api.get("/payments/i2v/balance");
            setCredits(data.credits);
        } catch {}
    }, [isLoggedIn]);

    useEffect(() => {
        load().finally(() => setLoading(false));
        const i = setInterval(load, 3000);
        return () => clearInterval(i);
    }, [load]);

    useEffect(() => { loadBalance(); }, [loadBalance]);

    const register = async (tid, buy_in) => {
        const { data } = await api.post(`/poker/tournaments/${tid}/register`, { buy_in });
        setCredits(data.credits);
        await load();
    };

    const unregister = async (tid) => {
        await api.post(`/poker/tournaments/${tid}/unregister`);
        await loadBalance();
        await load();
    };

    const findMine = (t) => (isLoggedIn ? t.players?.find((p) => p.user_id === user.id) : null);

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/poker" data-testid="tournaments-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Heads-Up
            </Link>

            <div className="flex gap-2 mb-4">
                <Link to="/poker/tournaments" data-testid="tab-tournaments" className="brutal-btn bg-vibrantpink text-white text-xs py-2 px-3">
                    <Trophy className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Tournaments
                </Link>
                <Link to="/poker/leaderboard" data-testid="tab-leaderboard" className="brutal-btn bg-white text-ink text-xs py-2 px-3">
                    <Crown className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Leaderboard
                </Link>
            </div>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantpink border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Trophy className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Poker <span className="text-vibrantpink">Tournaments</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        Multi-table Full Hold'em · Blinds double every 2 minutes · <span className="font-heading font-bold text-ink">Winner takes 100%</span>
                    </p>
                </div>
                {isLoggedIn && credits != null && (
                    <div className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal flex-shrink-0">
                        <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                        <span data-testid="tournaments-credits" className="font-heading font-bold text-sm text-ink">{credits}</span>
                    </div>
                )}
            </div>

            {!isLoggedIn && !authLoading && (
                <div data-testid="tournaments-login-gate" className="brutal-card p-5 bg-vibrantyellow mb-5">
                    <p className="font-heading font-bold text-sm text-ink">
                        <Link to="/login" className="text-vibrantorange hover:underline"><LogIn className="w-4 h-4 inline mr-1" strokeWidth={2.5} />Log in</Link> to register for a tournament.
                    </p>
                </div>
            )}

            {loading ? (
                <div className="font-heading font-bold text-ink/60 text-center py-12">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" strokeWidth={2.5} />
                    Loading tournaments…
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <h2 className="font-heading font-black text-lg tracking-tighter text-ink mb-2 flex items-center gap-2">
                            <Clock className="w-4 h-4" strokeWidth={2.5} /> Live & Upcoming
                        </h2>
                        {tournaments.live.length === 0 ? (
                            <div className="brutal-card p-4 bg-cream text-center font-body text-sm text-ink/60">
                                No tournaments scheduled right now — check back soon.
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="tournaments-live">
                                {tournaments.live.map((t) => (
                                    <TournamentCard
                                        key={t.id}
                                        t={t}
                                        mine={findMine(t)}
                                        isLoggedIn={isLoggedIn}
                                        credits={credits}
                                        onRegister={register}
                                        onUnregister={unregister}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {tournaments.recent.length > 0 && (
                        <div>
                            <h2 className="font-heading font-black text-lg tracking-tighter text-ink mb-2 flex items-center gap-2">
                                <Trophy className="w-4 h-4" strokeWidth={2.5} /> Recent
                            </h2>
                            <div className="space-y-3" data-testid="tournaments-recent">
                                {tournaments.recent.map((t) => (
                                    <TournamentCard
                                        key={t.id}
                                        t={t}
                                        mine={null}
                                        isLoggedIn={isLoggedIn}
                                        credits={credits}
                                        onRegister={register}
                                        onUnregister={unregister}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
